// src/services/voiceAndPlace.js — two senders the web was missing: a VOICE MEMO
// and a LOCATION.
//
// Both existed on the phone and neither existed here, which is why the guest page
// had no microphone and no map pin to offer. They are written against the exact
// field names ChatModal.tsx writes and services/messages.js decrypts, because a
// message is a contract between four readers — this browser, the owner's browser,
// the phone, and the server's relay — and inventing a field name is how one of
// them ends up rendering a padlock forever.
//
//   audio     → audioUrl + audioPerDeviceKeys + senderDeviceId + duration + waveform
//   location  → locationPerDeviceKeys sealing JSON {lat, lng} + senderDeviceId
//
// 🟥 THE COORDINATES ARE SEALED, NOT STORED. The phone puts `{lat, lng}` inside
//    `locationPerDeviceKeys` and writes NO plaintext latitude/longitude on the
//    document — somebody's position is not metadata. This does the same, and the
//    decrypt side already knows how to open it (`_payload` in messages.js).

import { collection, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { sealForUsers } from '../crypto/e2ee';
import { relayNewMessage } from './socket';
import { uploadEncryptedBytes, writeChatDoc, newTempId } from './mediaUpload';
import { sealMembers } from './groupMembers';
import { setMessageDoc } from './sendDoc'; // 🖥📱 origin + my other devices' copy

/* ══ VOICE MEMO ════════════════════════════════════════════════════════════ */

/**
 * 🟢 A CHEAP WAVEFORM, COMPUTED HERE. Both clients draw `waveform` — an array of
 *    0..1 amplitudes — and a memo without one renders as a flat dead bar. Peak
 *    amplitude per bucket (not RMS) because a peak is what the eye reads as "loud",
 *    and it survives the aggressive downsampling below.
 */
export async function waveformFromBlob(blob, buckets = 40) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return [];
    const ctx = new Ctx();
    const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
    const data = buf.getChannelData(0);
    const per = Math.max(1, Math.floor(data.length / buckets));
    const out = [];
    for (let b = 0; b < buckets; b++) {
      let peak = 0;
      const start = b * per;
      for (let i = start; i < start + per && i < data.length; i++) {
        const v = Math.abs(data[i]);
        if (v > peak) peak = v;
      }
      out.push(Math.min(1, Number(peak.toFixed(3))));
    }
    try { ctx.close(); } catch { /* nothing to do */ }
    // a silent recording would draw nothing at all; give it a floor so the bar reads
    const max = Math.max(...out, 0.001);
    return out.map((v) => Math.max(0.06, Number((v / max).toFixed(3))));
  } catch {
    return [];   // a memo with no waveform is still a memo
  }
}

/**
 * Send a recorded voice memo.
 * @param blob      what MediaRecorder produced
 * @param duration  seconds (the recorder counted them; the file may not say)
 */
/**
 * 🟥 PICK THE CONTAINER THE OTHER END CAN OPEN, not just the one this browser
 *    likes. MediaRecorder defaults to WebM/Opus in Chrome, and **iOS cannot play
 *    WebM at all** — so a guest recording on a desktop Chrome and sending to an
 *    iPhone produces a memo that is perfectly valid and completely unplayable
 *    where it is going. MP4/AAC is the one both sides always understand, so it is
 *    tried first and WebM is the fallback for browsers that cannot record it.
 *
 *    Exported so the recorder and the sender cannot drift apart on this.
 */
export function bestRecordingMime() {
  const wanted = [
    'audio/mp4',                    // Safari always; Chrome on recent versions
    'audio/mp4;codecs=mp4a.40.2',
    'audio/webm;codecs=opus',       // Chrome / Firefox fallback
    'audio/webm',
    'audio/ogg;codecs=opus',
  ];
  for (const c of wanted) {
    if (window.MediaRecorder?.isTypeSupported?.(c)) return c;
  }
  return '';                        // let the browser choose; blob.type still tells us
}

export async function sendVoiceMessage({
  chat, myUid, myName, blob, duration, waveform, onProgress, reply, tempId, registerAbort,
}) {
  if (!chat?.id || !blob) throw new Error('nothing to send');
  const participants = sealMembers(chat);   // 🔐 active members only
  const isGroup = !!chat.isGroup;
  const clientTempId = tempId || newTempId();

  const bytes = new Uint8Array(await blob.arrayBuffer());
  const up = await uploadEncryptedBytes(bytes, (f) => onProgress?.(f * 0.95), registerAbort);

  const sealed = await sealForUsers(JSON.stringify({ k: up.k, n: up.n }), participants);
  if (!sealed || !Object.keys(sealed.perDeviceKeys || {}).length) {
    throw new Error("Couldn't secure that recording — it was not sent.");
  }

  const msgRef = doc(collection(db, 'chats', chat.id, 'messages'));
  const messageId = msgRef.id;
  const preview = '🎤 Voice message';
  const secs = Math.max(1, Math.round(Number(duration) || 0));

  await setMessageDoc(msgRef, {
    type: 'audio',
    clientTempId,
    audioUrl: up.url,
    audioPerDeviceKeys: sealed.perDeviceKeys, e2eeMedia: true,
    senderDeviceId: sealed.senderDeviceId,
    duration: secs,
    /* 🟢 SAY WHAT IT ACTUALLY IS. The upload has no file extension, so without
       this the receiver can only guess from `kind` — and a wrong guess makes the
       player show an error over audio that plays fine. */
    audioMime: blob.type || 'audio/mp4',
    waveform: Array.isArray(waveform) ? waveform : [],
    senderId: myUid,
    timestamp: serverTimestamp(),
    sent: true,
    delivered: false,
    seen: false,
    ...(isGroup ? { senderName: myName || 'Web', isGroup: true } : {}),
    ...(reply || {}),
  });

  relayNewMessage({
    chatId: chat.id, messageId, fromUserId: myUid, participants, isGroup,
    cloudText: preview, clientMsgId: clientTempId,
    extra: {
      type: 'audio',
      audioUrl: up.url,
      audioPerDeviceKeys: sealed.perDeviceKeys, e2eeMedia: true,
      senderDeviceId: sealed.senderDeviceId,
      duration: secs,
      audioMime: blob.type || 'audio/mp4',
      waveform: Array.isArray(waveform) ? waveform : [],
      ...(isGroup ? { senderName: myName || 'Web' } : {}),
      ...(reply || {}),
    },
  });

  await writeChatDoc({ chat, myUid, myName, messageId, preview });
  onProgress?.(1);
  return messageId;
}

/* ══ LOCATION ══════════════════════════════════════════════════════════════ */

/**
 * Ask the browser where we are.
 *
 * 🟢 CURRENT POSITION ONLY — no live sharing. Following someone live means
 *    holding a subscription open and re-publishing as they move; a browser tab
 *    that can be closed at any moment is the wrong place for that, and a frozen
 *    first fix presented as "live" would be a lie that looks like a bug. One
 *    honest pin instead.
 */
export function currentPosition({ timeout = 12000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('This browser cannot share a location.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => reject(new Error(
        err && err.code === 1
          ? 'Location permission was denied — allow it in your browser settings to share where you are.'
          : 'Could not get your location. Try again in a moment.',
      )),
      { enableHighAccuracy: true, timeout, maximumAge: 30000 },
    );
  });
}

/**
 * Turn coordinates into "Dearborn, MI".
 *
 * 🟢 OPENSTREETMAP'S NOMINATIM — no API key, and the same service the Vibes
 *    check-in picker already uses, so this project has one geocoder rather than
 *    two. Its usage policy asks for light, human-paced traffic: this runs once
 *    per location the user chooses to share, never on a loop.
 *
 * 🟥 A FAILURE HERE MUST NOT BLOCK THE SEND. The name is a courtesy on top of a
 *    pin that is already correct — offline, rate-limited or simply unnamed, the
 *    caller gets null and sends coordinates, which is the part that matters.
 */
export async function reverseGeocode(lat, lng) {
  try {
    const url = 'https://nominatim.openstreetmap.org/reverse'
      + `?format=jsonv2&zoom=14&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const j = await res.json();
    const a = j.address || {};
    const town = a.city || a.town || a.village || a.suburb || a.county || null;
    // the state's short form is what people actually say — "Dearborn, MI"
    const region = a['ISO3166-2-lvl4'] ? String(a['ISO3166-2-lvl4']).split('-')[1] : (a.state || null);
    const label = [town, region].filter(Boolean).join(', ');
    return label || j.name || j.display_name?.split(',').slice(0, 2).join(',') || null;
  } catch {
    return null;
  }
}

/** Send a pin. `{lat, lng}` is sealed; nothing about it is stored in the clear. */
export async function sendLocationMessage({ chat, myUid, myName, lat, lng, name, reply, tempId }) {
  if (!chat?.id || !Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error('no position');
  const participants = sealMembers(chat);   // 🔐 active members only
  const isGroup = !!chat.isGroup;
  const clientTempId = tempId || newTempId();

  const sealed = await sealForUsers(
    JSON.stringify({ lat, lng, ...(name ? { name } : {}) }), participants,
  );
  if (!sealed || !Object.keys(sealed.perDeviceKeys || {}).length) {
    throw new Error("Couldn't secure that location — it was not sent.");
  }

  const msgRef = doc(collection(db, 'chats', chat.id, 'messages'));
  const messageId = msgRef.id;
  const preview = '📍 Location';

  await setMessageDoc(msgRef, {
    type: 'location',
    clientTempId,
    locationPerDeviceKeys: sealed.perDeviceKeys,
    senderDeviceId: sealed.senderDeviceId,
    live: false,                 // see currentPosition() — this is a single fix
    senderId: myUid,
    timestamp: serverTimestamp(),
    sent: true,
    delivered: false,
    seen: false,
    ...(isGroup ? { senderName: myName || 'Web', isGroup: true } : {}),
    ...(reply || {}),
  });

  relayNewMessage({
    chatId: chat.id, messageId, fromUserId: myUid, participants, isGroup,
    cloudText: preview, clientMsgId: clientTempId,
    extra: {
      type: 'location',
      locationPerDeviceKeys: sealed.perDeviceKeys,
      senderDeviceId: sealed.senderDeviceId,
      live: false,
      ...(isGroup ? { senderName: myName || 'Web' } : {}),
      ...(reply || {}),
    },
  });

  await writeChatDoc({ chat, myUid, myName, messageId, preview });
  return messageId;
}
